import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightMedia } from "../../../../modules/content/components/DaylightMedia/DaylightMedia";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IProcessStepsBlockProps } from "./ProcessStepsBlock.props";

export const ProcessStepsBlock = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  image,
  imageCaption,
  parentClass,
  spacing,
  steps,
}: IProcessStepsBlockProps) => {
  if (!steps?.length) return null;
  return (
    <DaylightSection
      className={cn("daylight-surface-tiefsee", parentClass)}
      id={anchorId}
      spacing={spacing}
    >
      <div className="flex flex-col gap-12 md:gap-16">
        <DaylightCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          descriptionClassName="text-graphite"
          eyebrow={eyebrow}
          heading={heading}
          layout="rail"
        />
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          {image ? (
            <div className="relative min-w-0">
              <DaylightMedia
                aspectRatio={image.aspectRatio || "1:1"}
                media={image.image}
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-primary/20" />
              {imageCaption ? (
                <p className="daylight-label-micro absolute bottom-4 left-4 bg-terminal/60 px-3 py-2 text-graphite/80 backdrop-blur-sm">
                  {imageCaption}
                </p>
              ) : null}
            </div>
          ) : null}
          <ol className="flex min-w-0 flex-col">
            {steps.map((step, index) => (
              <li
                className="flex gap-6 border-t border-border py-8 last:border-b"
                key={index}
              >
                <div className="flex flex-col items-center pt-1">
                  {step.icon ? <Icon className="size-6 text-primary" name={step.icon} /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-2 flex items-baseline gap-4">
                    <span className="daylight-label text-graphite">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-daylight-display text-2xl font-semibold tracking-[-0.04em] text-graphite">
                      {step.title}
                    </h3>
                  </div>
                  {step.description ? (
                    <DaylightRichText
                      className="max-w-md leading-relaxed text-graphite"
                      content={step.description}
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </DaylightSection>
  );
};

ProcessStepsBlock.displayName = "Block::ProcessStepsBlock";
